/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Inbox,
    Briefcase,
    FileText,
    MessageSquareQuote,
    BarChart3,
    Settings,
    LogOut,
    Trash2,
    Plus,
    Save,
    X,
    Pencil,
    KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ASSETS } from "@/constants/site";

const TABS = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "leads", label: "Leads", icon: Inbox },
    { id: "services", label: "Services", icon: Briefcase },
    { id: "blog", label: "Blog Posts", icon: FileText },
    { id: "testimonials", label: "Testimonials", icon: MessageSquareQuote },
    { id: "impact", label: "Impact Stats", icon: BarChart3 },
    { id: "settings", label: "Site Settings", icon: Settings },
    { id: "security", label: "Security", icon: KeyRound },
];

function errMsg(e) {
    return formatApiErrorDetail(e.response?.data?.detail) || e.message;
}

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("overview");

    const handleLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    return (
        <div data-testid="admin-dashboard" className="min-h-screen flex bg-slate-50">
            <aside className="w-64 bg-[#0A192F] text-white flex flex-col">
                <Link to="/" className="p-6 border-b border-white/10 flex items-center gap-3">
                    <div className="bg-white p-1.5 rounded-sm">
                        <img src={ASSETS.logo} className="h-8 w-auto" alt="HWC" />
                    </div>
                    <div className="leading-tight">
                        <div className="font-serif font-bold">Hopewell</div>
                        <div className="text-[10px] tracking-widest text-[#D4AF37] uppercase">
                            Admin
                        </div>
                    </div>
                </Link>
                <nav className="flex-1 p-3 space-y-1">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.id;
                        return (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                data-testid={`admin-tab-${t.id}`}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm rounded-sm transition-colors ${
                                    active
                                        ? "bg-white/10 text-[#D4AF37]"
                                        : "text-slate-300 hover:bg-white/5"
                                }`}
                            >
                                <Icon className="w-4 h-4" /> {t.label}
                            </button>
                        );
                    })}
                </nav>
                <div className="p-4 border-t border-white/10">
                    <div className="text-xs text-slate-400">Signed in as</div>
                    <div className="text-sm font-semibold truncate">{user?.email}</div>
                    <button
                        onClick={handleLogout}
                        data-testid="admin-logout"
                        className="mt-3 w-full flex items-center justify-center gap-2 text-xs uppercase tracking-widest border border-white/20 hover:border-[#D4AF37] hover:text-[#D4AF37] py-2 rounded-sm"
                    >
                        <LogOut className="w-3.5 h-3.5" /> Logout
                    </button>
                </div>
            </aside>

            <main className="flex-1 overflow-x-hidden">
                <header className="bg-white border-b border-slate-200 px-8 py-5 flex items-center justify-between">
                    <h1 className="font-serif text-2xl font-bold text-[#0A192F]">
                        {TABS.find((t) => t.id === tab)?.label}
                    </h1>
                </header>
                <div className="p-8">
                    {tab === "overview" && <OverviewPanel />}
                    {tab === "leads" && <LeadsPanel />}
                    {tab === "services" && <ServicesPanel />}
                    {tab === "blog" && <BlogPanel />}
                    {tab === "testimonials" && <TestimonialsPanel />}
                    {tab === "impact" && <ImpactPanel />}
                    {tab === "settings" && <SettingsPanel />}
                    {tab === "security" && <SecurityPanel />}
                </div>
            </main>
        </div>
    );
}

/* --------------------------- Overview ---------------------------------- */
function OverviewPanel() {
    const [counts, setCounts] = useState({});
    useEffect(() => {
        Promise.all([
            api.get("/admin/leads"),
            api.get("/admin/services"),
            api.get("/admin/blog"),
        ])
            .then(([leads, services, blog]) => {
                setCounts({
                    leads: leads.data.length,
                    new_leads: leads.data.filter((l) => l.status === "new").length,
                    services: services.data.length,
                    posts: blog.data.length,
                });
            })
            .catch(() => {});
    }, []);

    const cards = [
        { label: "Total Leads", value: counts.leads ?? "—", testid: "stat-leads" },
        { label: "New Leads", value: counts.new_leads ?? "—", testid: "stat-new-leads" },
        { label: "Active Services", value: counts.services ?? "—", testid: "stat-services" },
        { label: "Blog Posts", value: counts.posts ?? "—", testid: "stat-posts" },
    ];

    return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {cards.map((c) => (
                <div
                    key={c.label}
                    data-testid={c.testid}
                    className="bg-white border border-slate-200 p-6 rounded-sm"
                >
                    <div className="text-xs uppercase tracking-widest text-slate-500">
                        {c.label}
                    </div>
                    <div className="mt-3 font-serif text-4xl font-bold text-[#0A192F]">
                        {c.value}
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ---------------------------- Leads ------------------------------------ */
function LeadsPanel() {
    const [leads, setLeads] = useState([]);
    const [selected, setSelected] = useState(null);

    const load = () =>
        api
            .get("/admin/leads")
            .then(({ data }) => setLeads(data))
            .catch((e) => toast.error(errMsg(e)));

    useEffect(() => { load(); }, []);

    const updateStatus = async (lead, status) => {
        try {
            const { data } = await api.put(`/admin/leads/${lead.id}`, { status });
            setLeads((ls) => ls.map((l) => (l.id === data.id ? data : l)));
            if (selected?.id === data.id) setSelected(data);
            toast.success("Lead updated");
        } catch (e) { toast.error(errMsg(e)); }
    };

    const remove = async (lead) => {
        if (!window.confirm(`Delete lead from ${lead.name}?`)) return;
        try {
            await api.delete(`/admin/leads/${lead.id}`);
            setLeads((ls) => ls.filter((l) => l.id !== lead.id));
            if (selected?.id === lead.id) setSelected(null);
            toast.success("Lead deleted");
        } catch (e) { toast.error(errMsg(e)); }
    };

    return (
        <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 bg-white border border-slate-200">
                <div className="border-b border-slate-200 px-5 py-3 text-xs uppercase tracking-widest text-slate-500">
                    {leads.length} submission{leads.length !== 1 ? "s" : ""}
                </div>
                <ul className="divide-y divide-slate-200 max-h-[70vh] overflow-y-auto">
                    {leads.length === 0 && (
                        <li className="p-8 text-center text-sm text-slate-500">
                            No leads yet.
                        </li>
                    )}
                    {leads.map((l) => (
                        <li
                            key={l.id}
                            data-testid={`lead-row-${l.id}`}
                            onClick={() => setSelected(l)}
                            className={`p-5 cursor-pointer hover:bg-slate-50 ${selected?.id === l.id ? "bg-slate-50" : ""}`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="font-semibold text-[#0A192F]">
                                        {l.name}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        {l.email}
                                        {l.organization && ` · ${l.organization}`}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-1">
                                        {l.service_needed || "—"}
                                    </p>
                                </div>
                                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 ${
                                    l.status === "new" ? "bg-[#D4AF37]/20 text-[#876c00]" :
                                    l.status === "contacted" ? "bg-blue-100 text-blue-800" :
                                    "bg-slate-100 text-slate-600"
                                }`}>
                                    {l.status}
                                </span>
                            </div>
                            <p className="text-xs text-slate-400 mt-2">
                                {new Date(l.created_at).toLocaleString()}
                            </p>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="lg:col-span-5 bg-white border border-slate-200 p-6">
                {selected ? (
                    <div>
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="font-serif text-2xl font-bold text-[#0A192F]">
                                    {selected.name}
                                </h3>
                                <p className="text-xs text-slate-500 mt-1">
                                    {new Date(selected.created_at).toLocaleString()}
                                </p>
                            </div>
                            <button
                                onClick={() => remove(selected)}
                                data-testid="lead-delete"
                                className="text-slate-400 hover:text-red-600"
                                aria-label="Delete"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                        <dl className="mt-6 space-y-3 text-sm">
                            <Info label="Email" value={<a className="link-underline" href={`mailto:${selected.email}`}>{selected.email}</a>} />
                            <Info label="Phone" value={selected.phone ? <a className="link-underline" href={`tel:${selected.phone}`}>{selected.phone}</a> : "—"} />
                            <Info label="Organization" value={selected.organization || "—"} />
                            <Info label="Service Needed" value={selected.service_needed || "—"} />
                            <Info label="Budget" value={selected.budget_range || "—"} />
                            <Info label="Timeline" value={selected.timeline || "—"} />
                        </dl>
                        <div className="mt-6">
                            <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">
                                Message
                            </p>
                            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                                {selected.message}
                            </p>
                        </div>
                        <div className="mt-6 flex flex-wrap gap-2">
                            {["new", "contacted", "qualified", "closed"].map((s) => (
                                <button
                                    key={s}
                                    onClick={() => updateStatus(selected, s)}
                                    data-testid={`lead-status-${s}`}
                                    className={`text-[10px] uppercase tracking-widest px-3 py-2 border ${
                                        selected.status === s
                                            ? "bg-[#0A192F] text-white border-[#0A192F]"
                                            : "border-slate-300 text-slate-600 hover:border-[#0A192F]"
                                    }`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-slate-500 text-center py-10">
                        Select a lead to view details.
                    </p>
                )}
            </div>
        </div>
    );
}

function Info({ label, value }) {
    return (
        <div className="flex justify-between gap-4 border-b border-slate-100 pb-2">
            <span className="text-xs uppercase tracking-widest text-slate-500">{label}</span>
            <span className="text-sm text-slate-700 text-right">{value}</span>
        </div>
    );
}

/* --------------------------- Services ---------------------------------- */
function ServicesPanel() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/admin/services").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);

    const blank = {
        title: "", slug: "", description: "", who_it_is_for: "",
        deliverables: [], icon: "Briefcase", order: items.length + 1, active: true,
    };

    const save = async (svc) => {
        try {
            const payload = { ...svc, deliverables: Array.isArray(svc.deliverables) ? svc.deliverables : svc.deliverables.split("\n").map((s) => s.trim()).filter(Boolean) };
            if (svc.id) {
                await api.put(`/admin/services/${svc.id}`, payload);
                toast.success("Service updated");
            } else {
                await api.post("/admin/services", payload);
                toast.success("Service created");
            }
            setEditing(null);
            load();
        } catch (e) { toast.error(errMsg(e)); }
    };

    const remove = async (svc) => {
        if (!window.confirm(`Delete ${svc.title}?`)) return;
        try {
            await api.delete(`/admin/services/${svc.id}`);
            toast.success("Service deleted");
            load();
        } catch (e) { toast.error(errMsg(e)); }
    };

    return (
        <div>
            <div className="flex justify-end mb-5">
                <button
                    onClick={() => setEditing(blank)}
                    data-testid="add-service-btn"
                    className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-4 py-2.5 text-xs uppercase tracking-widest font-bold rounded-sm hover:bg-[#1E3A8A]"
                >
                    <Plus className="w-4 h-4" /> Add Service
                </button>
            </div>

            <div className="bg-white border border-slate-200 divide-y divide-slate-200">
                {items.map((s) => (
                    <div key={s.id} className="p-5 flex items-start justify-between gap-4">
                        <div>
                            <p className="font-semibold text-[#0A192F]">{s.title}</p>
                            <p className="text-xs text-slate-500 mt-1">{s.slug} · order {s.order}</p>
                            <p className="text-sm text-slate-600 mt-2 line-clamp-2">{s.description}</p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => setEditing(s)} className="text-slate-400 hover:text-[#0A192F]" data-testid={`edit-service-${s.id}`}>
                                <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => remove(s)} className="text-slate-400 hover:text-red-600">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {editing && <ServiceEditor svc={editing} onClose={() => setEditing(null)} onSave={save} />}
        </div>
    );
}

function ServiceEditor({ svc, onClose, onSave }) {
    const [form, setForm] = useState({
        ...svc,
        deliverables: Array.isArray(svc.deliverables) ? svc.deliverables.join("\n") : (svc.deliverables || ""),
    });
    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    return (
        <Modal title={svc.id ? "Edit Service" : "Add Service"} onClose={onClose}>
            <div className="space-y-5">
                <Field label="Title"><input className="exec-input" value={form.title} onChange={(e) => update("title", e.target.value)} data-testid="service-input-title" /></Field>
                <Field label="Slug (URL)"><input className="exec-input" value={form.slug} onChange={(e) => update("slug", e.target.value.toLowerCase().replace(/\s+/g, "-"))} /></Field>
                <Field label="Description"><textarea rows={3} className="exec-input" value={form.description} onChange={(e) => update("description", e.target.value)} /></Field>
                <Field label="Who it is for"><input className="exec-input" value={form.who_it_is_for} onChange={(e) => update("who_it_is_for", e.target.value)} /></Field>
                <Field label="Deliverables (one per line)"><textarea rows={4} className="exec-input" value={form.deliverables} onChange={(e) => update("deliverables", e.target.value)} /></Field>
                <div className="grid grid-cols-2 gap-4">
                    <Field label="Icon (lucide)"><input className="exec-input" value={form.icon} onChange={(e) => update("icon", e.target.value)} placeholder="Briefcase" /></Field>
                    <Field label="Order"><input type="number" className="exec-input" value={form.order} onChange={(e) => update("order", Number(e.target.value))} /></Field>
                </div>
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.active} onChange={(e) => update("active", e.target.checked)} /> Active
                </label>
            </div>
            <ModalActions onClose={onClose} onSave={() => onSave(form)} />
        </Modal>
    );
}

/* ----------------------------- Blog ------------------------------------ */
function BlogPanel() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/admin/blog").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);

    const blank = {
        title: "", slug: "", excerpt: "", content: "",
        category: "Business Development", cover_image: "",
        author: "Reico S. Hopewell", published: true,
    };

    const save = async (p) => {
        try {
            if (p.id) await api.put(`/admin/blog/${p.id}`, p);
            else await api.post("/admin/blog", p);
            toast.success("Saved");
            setEditing(null);
            load();
        } catch (e) { toast.error(errMsg(e)); }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.title}"?`)) return;
        try {
            await api.delete(`/admin/blog/${p.id}`);
            toast.success("Deleted");
            load();
        } catch (e) { toast.error(errMsg(e)); }
    };

    return (
        <div>
            <div className="flex justify-end mb-5">
                <button onClick={() => setEditing(blank)} data-testid="add-post-btn" className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-4 py-2.5 text-xs uppercase tracking-widest font-bold rounded-sm">
                    <Plus className="w-4 h-4" /> New Post
                </button>
            </div>

            <div className="bg-white border border-slate-200 divide-y">
                {items.map((p) => (
                    <div key={p.id} className="p-5 flex items-start justify-between gap-4">
                        <div>
                            <p className="font-semibold text-[#0A192F]">{p.title}</p>
                            <p className="text-xs text-slate-500 mt-1">{p.category} · {p.published ? "Published" : "Draft"}</p>
                            <p className="text-sm text-slate-600 mt-2 line-clamp-2">{p.excerpt}</p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => setEditing(p)} className="text-slate-400 hover:text-[#0A192F]">
                                <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => remove(p)} className="text-slate-400 hover:text-red-600">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {editing && <BlogEditor post={editing} onClose={() => setEditing(null)} onSave={save} />}
        </div>
    );
}

function BlogEditor({ post, onClose, onSave }) {
    const [form, setForm] = useState(post);
    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    return (
        <Modal title={post.id ? "Edit Post" : "New Post"} onClose={onClose}>
            <div className="space-y-5">
                <Field label="Title"><input className="exec-input" value={form.title} onChange={(e) => update("title", e.target.value)} /></Field>
                <Field label="Slug"><input className="exec-input" value={form.slug} onChange={(e) => update("slug", e.target.value.toLowerCase().replace(/\s+/g, "-"))} /></Field>
                <Field label="Category">
                    <select className="exec-input" value={form.category} onChange={(e) => update("category", e.target.value)}>
                        {["Business Development", "Nonprofit Strategy", "Real Estate", "Grant Writing", "Community Development", "Leadership"].map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                </Field>
                <Field label="Cover image URL"><input className="exec-input" value={form.cover_image} onChange={(e) => update("cover_image", e.target.value)} /></Field>
                <Field label="Excerpt"><textarea rows={2} className="exec-input" value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} /></Field>
                <Field label="Content (use blank lines to separate paragraphs)"><textarea rows={12} className="exec-input" value={form.content} onChange={(e) => update("content", e.target.value)} /></Field>
                <Field label="Author"><input className="exec-input" value={form.author} onChange={(e) => update("author", e.target.value)} /></Field>
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.published} onChange={(e) => update("published", e.target.checked)} /> Published
                </label>
            </div>
            <ModalActions onClose={onClose} onSave={() => onSave(form)} />
        </Modal>
    );
}

/* ------------------------ Testimonials --------------------------------- */
function TestimonialsPanel() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/testimonials").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);

    const blank = { author: "", role: "", organization: "", content: "", rating: 5, active: true };

    const save = async (t) => {
        try {
            if (t.id) await api.put(`/admin/testimonials/${t.id}`, t);
            else await api.post("/admin/testimonials", t);
            toast.success("Saved");
            setEditing(null);
            load();
        } catch (e) { toast.error(errMsg(e)); }
    };

    const remove = async (t) => {
        if (!window.confirm(`Delete testimonial from ${t.author}?`)) return;
        try {
            await api.delete(`/admin/testimonials/${t.id}`);
            toast.success("Deleted");
            load();
        } catch (e) { toast.error(errMsg(e)); }
    };

    return (
        <div>
            <div className="flex justify-end mb-5">
                <button onClick={() => setEditing(blank)} className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-4 py-2.5 text-xs uppercase tracking-widest font-bold rounded-sm" data-testid="add-testimonial-btn">
                    <Plus className="w-4 h-4" /> Add Testimonial
                </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
                {items.map((t) => (
                    <div key={t.id} className="bg-white border border-slate-200 p-5">
                        <p className="text-sm text-slate-700 italic">"{t.content}"</p>
                        <p className="mt-4 font-semibold text-[#0A192F]">{t.author}</p>
                        <p className="text-xs text-slate-500">{t.role} {t.organization && `· ${t.organization}`}</p>
                        <div className="mt-4 flex gap-2">
                            <button onClick={() => setEditing(t)} className="text-slate-400 hover:text-[#0A192F]"><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => remove(t)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                        </div>
                    </div>
                ))}
            </div>
            {editing && (
                <Modal title={editing.id ? "Edit Testimonial" : "Add Testimonial"} onClose={() => setEditing(null)}>
                    <div className="space-y-5">
                        <Field label="Content"><textarea rows={4} className="exec-input" value={editing.content} onChange={(e) => setEditing((p) => ({ ...p, content: e.target.value }))} /></Field>
                        <Field label="Author"><input className="exec-input" value={editing.author} onChange={(e) => setEditing((p) => ({ ...p, author: e.target.value }))} /></Field>
                        <Field label="Role"><input className="exec-input" value={editing.role} onChange={(e) => setEditing((p) => ({ ...p, role: e.target.value }))} /></Field>
                        <Field label="Organization"><input className="exec-input" value={editing.organization} onChange={(e) => setEditing((p) => ({ ...p, organization: e.target.value }))} /></Field>
                    </div>
                    <ModalActions onClose={() => setEditing(null)} onSave={() => save(editing)} />
                </Modal>
            )}
        </div>
    );
}

/* ------------------------- Impact -------------------------------------- */
function ImpactPanel() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/impact").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);

    const blank = { label: "", value: "", description: "", order: items.length + 1 };

    const save = async (s) => {
        try {
            if (s.id) await api.put(`/admin/impact/${s.id}`, s);
            else await api.post("/admin/impact", s);
            toast.success("Saved");
            setEditing(null);
            load();
        } catch (e) { toast.error(errMsg(e)); }
    };

    const remove = async (s) => {
        if (!window.confirm(`Delete stat "${s.label}"?`)) return;
        try {
            await api.delete(`/admin/impact/${s.id}`);
            toast.success("Deleted");
            load();
        } catch (e) { toast.error(errMsg(e)); }
    };

    return (
        <div>
            <div className="flex justify-end mb-5">
                <button onClick={() => setEditing(blank)} className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-4 py-2.5 text-xs uppercase tracking-widest font-bold rounded-sm" data-testid="add-stat-btn">
                    <Plus className="w-4 h-4" /> Add Stat
                </button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {items.map((s) => (
                    <div key={s.id} className="bg-white border border-slate-200 p-6">
                        <div className="font-serif text-4xl font-bold text-[#D4AF37]">{s.value}</div>
                        <p className="text-xs uppercase tracking-widest text-[#0A192F] mt-3">{s.label}</p>
                        {s.description && <p className="text-xs text-slate-500 mt-1">{s.description}</p>}
                        <div className="mt-4 flex gap-2">
                            <button onClick={() => setEditing(s)} className="text-slate-400 hover:text-[#0A192F]"><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => remove(s)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                        </div>
                    </div>
                ))}
            </div>
            {editing && (
                <Modal title={editing.id ? "Edit Stat" : "Add Stat"} onClose={() => setEditing(null)}>
                    <div className="space-y-5">
                        <Field label="Label"><input className="exec-input" value={editing.label} onChange={(e) => setEditing((p) => ({ ...p, label: e.target.value }))} /></Field>
                        <Field label="Value (display)"><input className="exec-input" value={editing.value} onChange={(e) => setEditing((p) => ({ ...p, value: e.target.value }))} placeholder="e.g. 700+" /></Field>
                        <Field label="Description"><input className="exec-input" value={editing.description} onChange={(e) => setEditing((p) => ({ ...p, description: e.target.value }))} /></Field>
                        <Field label="Order"><input type="number" className="exec-input" value={editing.order} onChange={(e) => setEditing((p) => ({ ...p, order: Number(e.target.value) }))} /></Field>
                    </div>
                    <ModalActions onClose={() => setEditing(null)} onSave={() => save(editing)} />
                </Modal>
            )}
        </div>
    );
}

/* ------------------------- Settings ------------------------------------ */
function SettingsPanel() {
    const [form, setForm] = useState(null);

    useEffect(() => {
        api.get("/settings").then(({ data }) => setForm(data));
    }, []);

    const save = async () => {
        try {
            await api.put("/admin/settings", form);
            toast.success("Settings saved");
        } catch (e) { toast.error(errMsg(e)); }
    };

    if (!form) return <p className="text-sm text-slate-500">Loading…</p>;
    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    return (
        <div className="bg-white border border-slate-200 p-8 max-w-3xl">
            <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Display Phone"><input className="exec-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} /></Field>
                <Field label="Phone Link (tel:)"><input className="exec-input" value={form.phone_link} onChange={(e) => update("phone_link", e.target.value)} /></Field>
                <Field label="Email"><input className="exec-input" value={form.email} onChange={(e) => update("email", e.target.value)} /></Field>
                <Field label="Address"><input className="exec-input" value={form.address} onChange={(e) => update("address", e.target.value)} /></Field>
                <Field label="Facebook URL"><input className="exec-input" value={form.facebook_url} onChange={(e) => update("facebook_url", e.target.value)} /></Field>
                <Field label="Instagram URL"><input className="exec-input" value={form.instagram_url} onChange={(e) => update("instagram_url", e.target.value)} /></Field>
                <Field label="LinkedIn URL"><input className="exec-input" value={form.linkedin_url || ""} onChange={(e) => update("linkedin_url", e.target.value)} /></Field>
            </div>
            <div className="mt-8 space-y-5">
                <Field label="Hero Headline"><input className="exec-input" value={form.hero_headline} onChange={(e) => update("hero_headline", e.target.value)} /></Field>
                <Field label="Hero Subheadline"><textarea rows={3} className="exec-input" value={form.hero_subheadline} onChange={(e) => update("hero_subheadline", e.target.value)} /></Field>
            </div>
            <button onClick={save} data-testid="save-settings" className="mt-8 inline-flex items-center gap-2 bg-[#0A192F] text-white px-6 py-3 text-xs uppercase tracking-widest font-bold rounded-sm hover:bg-[#1E3A8A]">
                <Save className="w-4 h-4" /> Save Settings
            </button>
        </div>
    );
}

/* -------------------------- Security --------------------------------- */
function SecurityPanel() {
    const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess(false);
        if (form.new_password !== form.confirm) {
            setError("New password and confirmation do not match.");
            return;
        }
        if (form.new_password.length < 10) {
            setError("New password must be at least 10 characters.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/change-password", {
                current_password: form.current_password,
                new_password: form.new_password,
            });
            setSuccess(true);
            setForm({ current_password: "", new_password: "", confirm: "" });
            toast.success("Password updated successfully.");
        } catch (e) {
            const msg = errMsg(e);
            setError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-white border border-slate-200 p-8 max-w-2xl">
            <div className="flex items-start gap-4 mb-8 pb-6 border-b border-slate-200">
                <div className="w-12 h-12 bg-[#0A192F] flex items-center justify-center rounded-sm flex-shrink-0">
                    <KeyRound className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                    <h2 className="font-serif text-xl font-bold text-[#0A192F]">
                        Change Password
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        We recommend updating the seeded password to something
                        only you know. Minimum 10 characters.
                    </p>
                </div>
            </div>

            <form onSubmit={onSubmit} className="space-y-6" data-testid="change-password-form">
                <Field label="Current Password">
                    <input
                        type="password"
                        required
                        data-testid="cp-current"
                        className="exec-input"
                        value={form.current_password}
                        onChange={(e) => update("current_password", e.target.value)}
                    />
                </Field>
                <Field label="New Password (min 10 chars)">
                    <input
                        type="password"
                        required
                        minLength={10}
                        data-testid="cp-new"
                        className="exec-input"
                        value={form.new_password}
                        onChange={(e) => update("new_password", e.target.value)}
                    />
                </Field>
                <Field label="Confirm New Password">
                    <input
                        type="password"
                        required
                        data-testid="cp-confirm"
                        className="exec-input"
                        value={form.confirm}
                        onChange={(e) => update("confirm", e.target.value)}
                    />
                </Field>

                {error && (
                    <p className="text-sm text-red-600" data-testid="cp-error">
                        {error}
                    </p>
                )}
                {success && (
                    <p className="text-sm text-green-700" data-testid="cp-success">
                        Password updated. Use the new password next time you log in.
                    </p>
                )}

                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="cp-submit"
                    className="inline-flex items-center gap-2 bg-[#0A192F] text-white hover:bg-[#1E3A8A] disabled:opacity-60 transition-colors rounded-sm px-6 py-3 text-xs uppercase tracking-widest font-bold"
                >
                    {submitting ? "Updating…" : "Update Password"}
                </button>
            </form>

            <div className="mt-8 pt-6 border-t border-slate-200">
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">
                    Security Notes
                </p>
                <ul className="text-xs text-slate-500 space-y-1.5 list-disc pl-4">
                    <li>Login attempts are rate-limited: 5 failed attempts locks your IP for 15 minutes.</li>
                    <li>Passwords are stored hashed with bcrypt — never as plain text.</li>
                    <li>Sessions expire after 12 hours and require re-login.</li>
                </ul>
            </div>
        </div>
    );
}

/* -------------------------- Modal helpers ------------------------------ */
function Modal({ title, children, onClose }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-sm">
                <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                    <h3 className="font-serif text-xl font-bold text-[#0A192F]">{title}</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-[#0A192F]"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-6">{children}</div>
            </div>
        </div>
    );
}

function ModalActions({ onClose, onSave }) {
    return (
        <div className="mt-8 flex justify-end gap-3">
            <button onClick={onClose} className="text-xs uppercase tracking-widest px-5 py-3 border border-slate-300 text-slate-600 hover:border-[#0A192F]">Cancel</button>
            <button onClick={onSave} data-testid="modal-save" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-5 py-3 bg-[#0A192F] text-white hover:bg-[#1E3A8A] font-bold">
                <Save className="w-4 h-4" /> Save
            </button>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="exec-input-label">{label}</label>
            {children}
        </div>
    );
}
